import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getExpenses, getExpensesInRange, deleteExpenseById, createExpense, updateExpense } from "../api/expense";
import { reportKeys } from "./reports";

export const expenseKeys = {
  all: ["expenses"] as const,
  list: (categoryId?: string) => [...expenseKeys.all, categoryId ?? "all"] as const,
  range: (start: string, end: string) => [...expenseKeys.all, "range", start, end] as const,
};

export function useExpensesQuery(categoryId?: string) {
  return useQuery({
    queryKey: expenseKeys.list(categoryId),
    queryFn: () => getExpenses(categoryId),
    staleTime: 30_000,
  });
}

export function useExpensesInRange(startDate: Date, endDate: Date) {
  const key = expenseKeys.range(startDate.toISOString(), endDate.toISOString());
  return useQuery({
    queryKey: key,
    queryFn: () => getExpensesInRange(startDate, endDate),
    enabled: !!startDate && !!endDate,
    staleTime: 30_000,
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string }) => deleteExpenseById(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expenseKeys.all as unknown as string[] });
      // Invalidate reports since they depend on expenses
      qc.invalidateQueries({ queryKey: reportKeys.all as unknown as string[] });
    },
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ date, amount, description, categoryTitle }: { date: Date; amount: number; description: string; categoryTitle: string }) =>
      createExpense(date, amount, description, categoryTitle),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expenseKeys.all as unknown as string[] });
      // Invalidate reports since they depend on expenses
      qc.invalidateQueries({ queryKey: reportKeys.all as unknown as string[] });
    },
  });
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, date, amount, description, categoryTitle }: { id: string; date: Date; amount: number; description: string; categoryTitle: string }) =>
      updateExpense(id, date, amount, description, categoryTitle),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expenseKeys.all as unknown as string[] });
      // Invalidate reports since they depend on expenses
      qc.invalidateQueries({ queryKey: reportKeys.all as unknown as string[] });
    },
  });
}


